/**
 * shareCard/types — the ShareCard v1 wire contract.
 *
 * One frozen shape that every share surface reads and every producer writes:
 * the swim card, the single-set card, the story line on each, and the codec
 * (codec.ts) that packs one into a URL path segment. Lanes build against THIS
 * file; changing a required field is a version bump, not an edit.
 *
 * Vocabulary reuse (deliberate, not duplication):
 *   - `ShareSetBlock.stroke` is a key of STROKE_COLORS (designTokens.ts) —
 *     "Freestyle" | "Backstroke" | ... plus the drill-label chips. Typed as
 *     `string` because STROKE_COLORS is a Record<string, string>, so `keyof`
 *     would widen to string anyway; the card renderer looks the color up by
 *     this exact key.
 *   - `ShareSet.name` is a SetGroup.label (setGroups.ts) — "The Rockalita",
 *     "Warmup", "Freestyle set" — so a shared card reads with the same name
 *     the ribbon and the ledger use.
 *   - `ShareSetBlock.role` maps from SetGroup.setId's bookend ids: "warmup"
 *     and "cooldown" carry over verbatim, every other setId is "main", and
 *     "other" covers a block that is neither (a lone length, a drill).
 *
 * NOT reused: setGroups has no reps × distance type. SetGroup is LAP-level
 * (a Lap[] plus rolled-up meters/duration); a share card is a denormalized
 * summary — "4 × 50 free" — that must survive without the lap array. So
 * ShareSetBlock is new here rather than an alias of anything in setGroups.
 *
 * Pure module (no Svelte runtime, no external deps), per lib convention.
 */

export const SHARE_CARD_VERSION = 1;

/**
 * Swim cards only: v2 adds `owner` (VP polish ruling, 2026-09-10 07:xx: the
 * whole-swim card names the swimmer on a card someone else opens). A swim
 * card is written as v2 ONLY when it carries an owner, so an anonymous swim
 * card stays v1 and every v1 reader keeps opening it; set cards stay v1
 * (SHARE_CARD_VERSION), so the open-set flow and the iOS deep link are
 * untouched. A v1 swim card decodes as before: no owner, anonymous.
 */
export const SWIM_CARD_VERSION = 2;

/** Which fact the card's one headline sentence is making. */
export type StoryLineKind =
  | "new-set"
  | "fastest-set"
  | "new-sequence"
  | "nth-time"
  | "pr-distance"
  | "clubmates"
  | "date";

/** The headline on a card: one line, with an optional second line of evidence. */
export interface StoryLine {
  kind: StoryLineKind;
  /** The one line the card shows. */
  text: string;
  /** Optional second line, e.g. "beat 1:41 from Aug 2". */
  detail?: string;
}

/** One run inside a set: "4 × 50 Freestyle". */
export interface ShareSetBlock {
  /** Display label for the run ("4 × 50 free", "25 fly"). */
  label: string;
  reps: number;
  /** Metres in ONE rep, not the block total. */
  distanceM: number;
  /** A STROKE_COLORS key (designTokens.ts). */
  stroke: string;
  paceSecPer100?: number;
  intervalSec?: number;
  role: "warmup" | "main" | "cooldown" | "other";
}

/** A named set as it appears on a card. `name` is a SetGroup.label. */
export interface ShareSet {
  name: string;
  blocks: ShareSetBlock[];
  totalM: number;
  /** Cross-swim history for this set, when the ledger knows it. */
  lineage?: { timesSwum: number; bestPaceSecPer100?: number };
  /**
   * Heart rate in the set's window, as the set detail sheet shows it: avg and
   * peak BPM rounded, and how many of its lengths carry a sample (of the
   * lengths its blocks add up to). Absent when the window holds no samples.
   * Optional, so a card without it (every card before 2026-09-11) still reads.
   *
   * `perLength`: one rounded BPM per length in swim order (null for a length
   * with no sample), the swim card's heart-rate line. `restAfter`: the rounded
   * BPM in the gap before the next set, null when there is no next set or no
   * sample in the gap. Both optional: a card from before the line still reads.
   */
  hr?: { avg: number; peak: number; lengths: number; perLength?: (number | null)[]; restAfter?: number | null };
}

/** Most per-length readings one set may carry: 10 km of 25 m lengths. A longer
 *  array is not a card (a hand-made link cannot make the line draw forever). */
export const MAX_HR_LENGTHS = 400;

export interface ShareSwimCard {
  /** 1, or 2 when `owner` is present (SWIM_CARD_VERSION). */
  v: 1 | 2;
  kind: "swim";
  /** ISO date, e.g. "2026-09-09". */
  date: string;
  poolM: number;
  distanceM: number;
  durationSec: number;
  avgHr?: number;
  sets: ShareSet[];
  story: StoryLine;
  /** First names only. */
  clubmates?: string[];
  /** v2 only: the swimmer's first name, so a card someone else opens reads "Scott's swim …". */
  owner?: string;
}

export interface ShareSetCard {
  v: 1;
  kind: "set";
  set: ShareSet;
  story: StoryLine;
  owner?: string;
}

export type ShareCard = ShareSwimCard | ShareSetCard;

const STORY_KINDS: readonly string[] = [
  "new-set",
  "fastest-set",
  "new-sequence",
  "nth-time",
  "pr-distance",
  "clubmates",
  "date",
];

const BLOCK_ROLES: readonly string[] = ["warmup", "main", "cooldown", "other"];

function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/** Present-and-right-type, or absent. An explicit `undefined` counts as absent. */
function optional(v: unknown, type: "string" | "number"): boolean {
  return v === undefined || typeof v === type;
}

function isStoryLine(x: unknown): x is StoryLine {
  if (!isObject(x)) return false;
  if (typeof x.kind !== "string" || !STORY_KINDS.includes(x.kind)) return false;
  if (typeof x.text !== "string") return false;
  return optional(x.detail, "string");
}

function isShareSetBlock(x: unknown): x is ShareSetBlock {
  if (!isObject(x)) return false;
  if (typeof x.label !== "string") return false;
  if (typeof x.reps !== "number" || typeof x.distanceM !== "number") return false;
  if (typeof x.stroke !== "string") return false;
  if (typeof x.role !== "string" || !BLOCK_ROLES.includes(x.role)) return false;
  return optional(x.paceSecPer100, "number") && optional(x.intervalSec, "number");
}

function isShareSet(x: unknown): x is ShareSet {
  if (!isObject(x)) return false;
  if (typeof x.name !== "string" || typeof x.totalM !== "number") return false;
  if (!Array.isArray(x.blocks) || !x.blocks.every(isShareSetBlock)) return false;
  if (x.lineage !== undefined) {
    if (!isObject(x.lineage)) return false;
    if (typeof x.lineage.timesSwum !== "number") return false;
    if (!optional(x.lineage.bestPaceSecPer100, "number")) return false;
  }
  if (x.hr !== undefined) {
    if (!isObject(x.hr)) return false;
    if (typeof x.hr.avg !== "number" || typeof x.hr.peak !== "number" || typeof x.hr.lengths !== "number") return false;
    const { perLength, restAfter } = x.hr;
    if (perLength !== undefined) {
      if (!Array.isArray(perLength) || perLength.length > MAX_HR_LENGTHS) return false;
      if (!perLength.every((b) => b === null || typeof b === "number")) return false;
    }
    if (restAfter !== undefined && restAfter !== null && typeof restAfter !== "number") return false;
  }
  return true;
}

/**
 * Structural guard for a decoded card. Checks the version, the kind tag, and
 * every REQUIRED field of that kind — so a v2 payload, a truncated card, or a
 * hand-edited URL is rejected before any renderer touches it.
 */
export function isShareCard(x: unknown): x is ShareCard {
  if (!isObject(x)) return false;
  if (!isStoryLine(x.story)) return false;

  if (x.kind === "swim") {
    if (x.v !== SHARE_CARD_VERSION && x.v !== SWIM_CARD_VERSION) return false;
    // `owner` arrived with v2; a v1 card that carries one was not written by any build.
    if (x.owner !== undefined && (x.v !== SWIM_CARD_VERSION || typeof x.owner !== "string")) return false;
    if (typeof x.date !== "string") return false;
    if (typeof x.poolM !== "number") return false;
    if (typeof x.distanceM !== "number") return false;
    if (typeof x.durationSec !== "number") return false;
    if (!Array.isArray(x.sets) || !x.sets.every(isShareSet)) return false;
    if (!optional(x.avgHr, "number")) return false;
    if (x.clubmates !== undefined) {
      if (!Array.isArray(x.clubmates) || !x.clubmates.every((n) => typeof n === "string")) return false;
    }
    return true;
  }

  if (x.kind === "set") {
    if (x.v !== SHARE_CARD_VERSION) return false;
    return isShareSet(x.set) && optional(x.owner, "string");
  }

  return false;
}
